import { useId, type MouseEvent } from "react";
import { cn } from "../../../lib/cn";
import { usePlaceAutocomplete } from "../lib/usePlaceAutocomplete";
import type { PlaceSuggestion } from "../types";
import styles from "./PropertyAddressField.module.css";

export interface PropertyAddressFieldProps {
  /** Current address text held by the draft. */
  value: string;
  /** Called on every keystroke — the address is free text until one is chosen. */
  onChange: (address: string) => void;
  /**
   * Called when a suggestion is chosen. Separate from `onChange` so the parent
   * can tell a verified pick apart from typed text and, later, keep the
   * coordinates that came with it.
   */
  onSelect?: (place: PlaceSuggestion) => void;
  /** Injectable lookup; defaults to the server-proxied geocoder. */
  search?: (query: string) => Promise<PlaceSuggestion[]>;
}

/**
 * Property address combobox with South Australian address autocomplete.
 *
 * The lookup runs through `GET /api/places`, so no geocoding key is exposed to
 * the browser and the provider can be swapped server-side without touching this
 * component. Free text is always allowed: the geocoder is an aid, not a gate —
 * a new subdivision or a shed on a rural block may simply not be in the index
 * yet, and the job still has to be raisable.
 *
 * Presentational only. Search, debouncing, and highlight state live in
 * {@link usePlaceAutocomplete}.
 *
 * @param {PropertyAddressFieldProps} props - Draft value, change callbacks, and lookup.
 */
export function PropertyAddressField({ value, onChange, onSelect, search }: PropertyAddressFieldProps) {
  const {
    rootRef,
    matches,
    activeIndex,
    error,
    loading,
    showList,
    handleInputChange,
    handleFocus,
    handleKeyDown,
    handleHighlight,
    handleChoose,
  } = usePlaceAutocomplete({ value, onChange, onSelect, search });

  // Generated, not literal: the form can hold more than one address field, and
  // duplicate ids break both the <label for> and the combobox's aria wiring.
  const inputId = useId();
  const listId = useId();
  const errorId = useId();

  // The highlight lives on the input's aria-activedescendant, not on DOM focus:
  // focus must stay in the box while the arrow keys walk the list.
  const optionId = (index: number): string => `${listId}-option-${index}`;

  return (
    <div className={styles.root} ref={rootRef}>
      <label className={styles.labelRow} htmlFor={inputId}>
        <span className={styles.label}>Property</span>
      </label>
      <input
        id={inputId}
        className={styles.input}
        name="propertyAddress"
        value={value}
        placeholder="Start typing a South Australian address…"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        aria-describedby={error ? errorId : undefined}
        role="combobox"
        autoComplete="off"
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />
      {error ? (
        <span id={errorId} role="status" className={styles.fieldError}>
          {error}. Type the address in full instead.
        </span>
      ) : null}
      {showList ? (
        <ul className={styles.list} id={listId} role="listbox">
          {loading && matches.length === 0 ? (
            <li className={cn(styles.item, styles.status)}>Searching addresses…</li>
          ) : null}
          {matches.map((place, index) => (
            <SuggestionRow
              key={place.id}
              id={optionId(index)}
              place={place}
              index={index}
              active={index === activeIndex}
              onHighlight={handleHighlight}
              onChoose={handleChoose}
            />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface SuggestionRowProps {
  /** Stable id the input points at via `aria-activedescendant` when highlighted. */
  id: string;
  place: PlaceSuggestion;
  /** Position in the listbox, reported back on hover to move the highlight. */
  index: number;
  active: boolean;
  onHighlight: (index: number) => void;
  onChoose: (place: PlaceSuggestion) => void;
}

/**
 * One address row in the suggestion listbox.
 *
 * Commits on `mousedown` with the default prevented, so the choice lands before
 * the input loses focus and closes the list out from under the click.
 */
function SuggestionRow({ id, place, index, active, onHighlight, onChoose }: SuggestionRowProps) {
  const handleMouseEnter = (): void => onHighlight(index);
  const handleMouseDown = (event: MouseEvent<HTMLLIElement>): void => {
    event.preventDefault();
    onChoose(place);
  };

  return (
    <li
      id={id}
      role="option"
      aria-selected={active}
      className={cn(styles.item, { [styles.itemActive]: active })}
      onMouseEnter={handleMouseEnter}
      onMouseDown={handleMouseDown}
    >
      <span className={styles.primary}>{place.label}</span>
      {place.detail ? <span className={styles.meta}>{place.detail}</span> : null}
    </li>
  );
}
